import { WorkingHours, WorkInterval } from '@chilipiper/api-client-user-availability'
import {
  add,
  differenceInMilliseconds,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parse,
  startOfDay,
  subtract,
} from '@chilipiper/date-time'
import { convertMsToMinutes } from '@chilipiper/utils'
import omit from 'lodash/omit'
import { fromPairs, sortBy, toPairs } from 'lodash'

const daysOrder: (keyof WorkingHours)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

export const getTimeInMilliseconds = (time: string) => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  return ((hours * 60 + minutes) * 60 + seconds) * 1000
}

export interface WorkIntervalsWithMs extends WorkInterval {
  endsAtMs: number
  startsAtMs: number
}

export const findOverlaps = (schedule: WorkingHours): WorkingHours => {
  return Object.entries(schedule).reduce((result, [day, intervals]) => {
    if (!intervals) {
      return result
    }

    const workIntervals = intervals as WorkInterval[]
    const formattedIntervals: WorkIntervalsWithMs[] = workIntervals
      .map(interval => ({
        ...interval,
        startsAtMs: getTimeInMilliseconds(interval.startsAt),
        endsAtMs: getTimeInMilliseconds(interval.startsAt) + parseInt(interval.duration, 10),
      }))
      .sort((a, b) => a.startsAtMs - b.startsAtMs)

    const overlaps = formattedIntervals.flatMap((currentInterval, index, collection) =>
      collection
        .slice(index + 1)
        .filter(nextInterval => currentInterval.endsAtMs > nextInterval.startsAtMs)
        .map(overlappingInterval => omit(overlappingInterval, ['startsAtMs', 'endsAtMs']))
    )

    if (overlaps.length > 0) {
      result[day] = overlaps
    }

    return result
  }, {})
}

export const addDurationToTimeString = (timeString: string, durationMs: number): string => {
  const initialDate = parse(timeString, 'HH:mm:ss', new Date(0))
  const newDate = add(initialDate, { minutes: convertMsToMinutes(durationMs) })
  return format(newDate, 'HH:mm:ss')
}

export const sortByDayOrder = (obj: WorkingHours) => {
  return fromPairs(sortBy(toPairs(obj), ([key, _]) => daysOrder.indexOf(key as keyof WorkingHours)))
}

const getStartEndDates = (startTime: string, endTime: string) => {
  const startDate = parse(startTime, 'HH:mm', new Date())
  const endDate = parse(
    endTime,
    'HH:mm',
    endTime === '00:00' ? add(new Date(), { days: 1 }) : new Date()
  )
  return { startDate, endDate }
}

export const calculateChangedStartTimeDifference = (newStartTime: string, endTime: string) => {
  const { startDate: newStartDate, endDate } = getStartEndDates(newStartTime, endTime)

  if (isAfter(newStartDate, endDate)) {
    const updatedEndTime = add(newStartDate, { hours: 1 })
    if (isSameDay(newStartDate, updatedEndTime)) {
      return differenceInMilliseconds(updatedEndTime, newStartDate)
    } else {
      const startOfNextDay = startOfDay(add(newStartDate, { days: 1 }))
      return differenceInMilliseconds(startOfNextDay, newStartDate)
    }
  }

  return differenceInMilliseconds(endDate, newStartDate)
}

export const calculateChangedEndTime = (startTime: string, newEndTime: string) => {
  const { startDate, endDate: newEndDate } = getStartEndDates(startTime, newEndTime)

  if (isBefore(newEndDate, startDate)) {
    const updatedStartTime = subtract(newEndDate, { hours: 1 })
    if (isSameDay(updatedStartTime, newEndDate)) {
      const diff = differenceInMilliseconds(newEndDate, updatedStartTime)
      return { newStartTime: format(updatedStartTime, 'HH:mm'), difference: diff }
    } else {
      const startOfDayDate = startOfDay(newEndDate)
      const diff = differenceInMilliseconds(newEndDate, startOfDayDate)
      return { newStartTime: format(startOfDayDate, 'HH:mm'), difference: diff }
    }
  }

  const difference = differenceInMilliseconds(newEndDate, startDate)
  return { newStartTime: startTime, difference }
}

/**
 * Returns an array of strings representing every 15 minutes
 *
 * @param midnightPlacement - 'start' or 'end'
 * @returns An array of strings representing every 15 minutes from 00:00 to 23:45 if
 *   midnightPlacement is 'start' or 00:15 to 00:00 if midnightPlacement is 'end'
 */
export const every15Minutes = (midnightPlacement: 'start' | 'end') => {
  const start = midnightPlacement === 'start' ? 0 : 15
  const end = midnightPlacement === 'start' ? 23 * 60 + 45 : 24 * 60
  const result: Array<string> = []
  for (let i = start; i <= end; i += 15) {
    result.push(
      `${(Math.floor(i / 60) % 24).toString().padStart(2, '0')}:${(i % 60)
        .toString()
        .padStart(2, '0')}`
    )
  }
  return result
}

export const formatTimeRange = (startTime: Date, endTime: Date) => {
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric', hour12: true }
  const formatter = new Intl.DateTimeFormat('default', options)
  const resolvedOptions = formatter.resolvedOptions()

  if (resolvedOptions.hour12) {
    options.hour12 = true
  }
  const timeFormatter = new Intl.DateTimeFormat('default', options)
  const formattedStartTime = timeFormatter.format(startTime)
  const formattedEndTime = timeFormatter.format(endTime)

  return `${formattedStartTime} - ${formattedEndTime}`.toUpperCase()
}
